import React, { FC, useEffect, useMemo, useState } from "react";
import styled from "@emotion/styled";
import { Button, Col, Modal, Row, Space, Spin, Typography, message } from "antd";
import { CheckCircleFilled } from "@ant-design/icons";
import { CheckShippingItemsItem, GetOrderDetailData } from "services/interface";
import { getSelfShippingBrands } from "services/ordersApi";
import { useGetOrderDetail } from "hooks/useOrders";
import { SelfShippingBrand, mapSelfShippingBrands } from "./selfTracking";
import SelfTrackingForm from "./SelfTrackingForm";

interface SelfTrackingModalProps {
  visible: boolean;
  orderId: string | null;
  /**
   * ชิ้น "ส่งเอง" ที่ยังเป็น factory_approve (จาก checkShippingItems)
   * null = backend ไม่ได้ส่งรายละเอียดต่อรายการ (ออเดอร์ส่งเองล้วน) → ใช้ทุกรายการ factory_approve ของออเดอร์
   */
  selfItems: CheckShippingItemsItem[] | null;
  onClose: () => void;
  /** เรียกหลังบันทึก (สำเร็จอย่างน้อย 1 รายการ) — ให้ตารางโหลดใหม่ */
  onSaved: () => void;
}

/**
 * กรอกเลขพัสดุจริงของชิ้น "ส่งเอง" ทั้งออเดอร์ในครั้งเดียว โดยไม่ต้องเรียกขนส่งก่อน (ฟอร์มอยู่ที่ SelfTrackingForm)
 * - backend สร้างแถวจัดส่ง (เลขอ้างอิง BB) ให้เองแล้วย้ายรายการไป wait_agent_confirm / wait_retail_confirm
 * (หลังเรียกขนส่งแล้ว ตารางใช้ ShippingResultModal ซึ่งกรอกเลขชิ้นส่งเองได้เช่นกัน)
 */
const SelfTrackingModal: FC<SelfTrackingModalProps> = ({ visible, orderId, selfItems, onClose, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [orderDetail, setOrderDetail] = useState<GetOrderDetailData | null>(null);
  const [brands, setBrands] = useState<SelfShippingBrand[]>([]);

  const [saving, setSaving] = useState(false);
  /** retail_order_item_id ที่บันทึกสำเร็จแล้วในรอบนี้ (ได้จาก SelfTrackingForm) */
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set());

  // เก็บรายการตอนเปิด modal — ตารางโหลดใหม่หลังบันทึกแล้วชิ้นที่บันทึกจะหลุดจาก selfItems
  const [openedItems, setOpenedItems] = useState<CheckShippingItemsItem[] | null>([]);
  const { mutate: fetchOrderDetail } = useGetOrderDetail();

  useEffect(() => {
    if (!visible || !orderId) {
      setOrderDetail(null);
      setOpenedItems([]);
      setSavedIds(new Set());
      return;
    }
    setOpenedItems(selfItems);
    setLoading(true);
    fetchOrderDetail(
      { key_type: "order_id", group_by: "factory", items: [orderId] },
      {
        onSuccess: (data) => {
          setOrderDetail(data?.data?.[0] || null);
          setLoading(false);
        },
        onError: () => {
          message.error("ไม่สามารถดึงข้อมูลคำสั่งซื้อได้");
          setLoading(false);
        },
      }
    );
    if (brands.length === 0) {
      getSelfShippingBrands()
        .then((res: any) => setBrands(mapSelfShippingBrands(res)))
        .catch(() => {});
    }
  }, [visible, orderId]); // eslint-disable-line react-hooks/exhaustive-deps

  // จับคู่ชิ้นส่งเองกับรายละเอียดสินค้า (order_item_id = retail_order_item.id) · ลำดับตามรายการสินค้าของออเดอร์
  const rows = useMemo(() => {
    const detailItems = orderDetail?.items || [];
    if (openedItems === null) {
      return detailItems
        .filter((it) => it.item_status === "factory_approve")
        .map((it) => ({ ...it, ship_qty: Number(it.sku_cnt ?? it.qty) || 1 }));
    }
    const qtyById = new Map(openedItems.map((i) => [i.retail_order_item_id, Number(i.item_qty) || 1]));
    return detailItems
      .filter((it) => qtyById.has(it.order_item_id))
      .map((it) => ({ ...it, ship_qty: qtyById.get(it.order_item_id) || 1 }));
  }, [orderDetail, openedItems]);

  const totalPieces = rows.reduce((sum, r) => sum + r.ship_qty, 0);

  const orderCode = orderDetail?.order_code || orderDetail?.items?.[0]?.order_code;
  const disabled = rows.length === 0;

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      destroyOnClose
      width={960}
      maskClosable={!saving}
      closable={!saving}
      title={
        <Typography.Title level={5} style={{ marginTop: 0 }}>
          กรอกเลขพัสดุ (ส่งเอง) เลขที่คำสั่งซื้อ :{orderCode ? ` ${orderCode}` : ""}
        </Typography.Title>
      }
      footer={
        <Button onClick={onClose} disabled={saving}>
          ปิดหน้าจอ
        </Button>
      }
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin />
        </div>
      ) : (
        <Row gutter={16}>
          <Col xs={24} md={13}>
            <HeaderTab>
              <Typography.Text style={{ paddingBottom: 5, paddingTop: 5 }}>
                สินค้าที่จัดส่งเอง ({totalPieces} ชิ้น)
              </Typography.Text>
            </HeaderTab>
            <div style={{ height: "55vh", overflowY: "auto", padding: 8 }}>
              {disabled ? (
                <div style={{ textAlign: "center", color: "#999", marginTop: 40 }}>ไม่มีรายการที่ต้องกรอกเลขพัสดุ</div>
              ) : (
                rows.map((row) => {
                  const isSaved = savedIds.has(row.order_item_id);
                  return (
                    <ItemCard key={row.order_item_id}>
                      <div style={{ display: "flex", gap: 12 }}>
                        <img
                          src={row.image_url || row.sku_image || row.product_image || "/placeholder-image.png"}
                          alt=""
                          style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 4, flexShrink: 0 }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                            <Typography.Text strong>{row.product_name}</Typography.Text>
                            <Typography.Text type="secondary" style={{ flexShrink: 0 }}>
                              จำนวน {row.ship_qty}
                            </Typography.Text>
                          </div>
                          <Typography.Text type="secondary" style={{ fontSize: 13, display: "block" }}>
                            {row.sku_description || row.sku_name}
                          </Typography.Text>
                          {/* บันทึกบางรายการแล้วบางรายการพัง — บอกว่าตัวไหนผ่านแล้ว */}
                          {isSaved && (
                            <Space size={4} style={{ marginTop: 4, fontSize: 13, color: "#389e0d" }}>
                              <CheckCircleFilled />
                              บันทึกแล้ว
                            </Space>
                          )}
                        </div>
                      </div>
                    </ItemCard>
                  );
                })
              )}
            </div>
          </Col>

          <Col xs={24} md={11}>
            <HeaderTab>
              <Typography.Text style={{ paddingBottom: 5, paddingTop: 5 }}>การดำเนินการ</Typography.Text>
            </HeaderTab>
            <div style={{ height: "55vh", display: "flex", flexDirection: "column", padding: 8, overflowY: "auto" }}>
              <SelfTrackingForm
                rows={rows}
                brands={brands}
                onSavingChange={setSaving}
                onSavedIdsChange={setSavedIds}
                onSaved={onSaved}
                onDone={onClose}
              />
            </div>
          </Col>
        </Row>
      )}
    </Modal>
  );
};

export default SelfTrackingModal;

const ItemCard = styled.div`
  padding: 12px;
  margin-bottom: 10px;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  background: #fafafa;
`;

const HeaderTab = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid #f0f0f0;
  border-top: 1px solid #f0f0f0;
`;
